<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DEPT_TO_DEPT_EN,
  EXPIRES_EN,
  EXPIRED_EN,
  PDF_SENT_EN,
  PAYMENT_IS_DUE_EN,
  NAME_REQUIRED_EN,
  PAYMENT_SUCCESS_EN,
  DEPT_TO_DEPT_FR,
  EXPIRES_FR,
  EXPIRED_FR,
  PDF_SENT_FR,
  PAYMENT_IS_DUE_FR,
  NAME_REQUIRED_FR,
  PAYMENT_SUCCESS_FR,
  DEPOSIT_DUE_EN,
  DEPOSIT_DUE_FR,
  MISSING_MARGIN_EN,
  MISSING_MARGIN_FR,
  BROCHURE_URL_EN,
  BROCHURE_URL_FR,
  DEPT_TO_DEPT_INCENTIVE,
  ALERT_SYNC_HOTEL
} from '@/constants';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useToast } from 'primevue/usetoast';
import SplitterPanel from 'primevue/splitterpanel';
import Splitter from 'primevue/splitter';
import Toast from 'primevue/toast';
import AdminSetupUrl from './AdminSetupUrl.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const { canEditAdminSetup } = usePromoComposable();
const configurationModule = useItemConfigurationStore();

const payload = reactive({
  urlType: '',
  link: ''
});

const inputType = ref('');

const link = reactive({
  [DEPOSIT_DUE_EN]: '',
  [DEPT_TO_DEPT_EN]: '',
  [EXPIRES_EN]: '',
  [EXPIRED_EN]: '',
  [PDF_SENT_EN]: '',
  [PAYMENT_IS_DUE_EN]: '',
  [NAME_REQUIRED_EN]: '',
  [PAYMENT_SUCCESS_EN]: '',
  [MISSING_MARGIN_EN]: '',
  [BROCHURE_URL_EN]: '',
  [DEPOSIT_DUE_FR]: '',
  [DEPT_TO_DEPT_FR]: '',
  [EXPIRES_FR]: '',
  [EXPIRED_FR]: '',
  [PDF_SENT_FR]: '',
  [PAYMENT_IS_DUE_FR]: '',
  [NAME_REQUIRED_FR]: '',
  [PAYMENT_SUCCESS_FR]: '',
  [MISSING_MARGIN_FR]: '',
  [BROCHURE_URL_FR]: '',
  [DEPT_TO_DEPT_INCENTIVE]: '',
  [ALERT_SYNC_HOTEL]: ''
});

const status = computed(() => configurationModule.selectStatus);
const itemUrl = computed(() => configurationModule.emailUrls);

onBeforeMount(() => {
  configurationModule.getListEmailUrls();
});

const handleSubmit = async (type: string) => {
  payload.urlType = type;
  payload.link = link[type];
  await configurationModule.updateUrlEmailByUrlType(payload);
  if (status.value === true) {
    toast.add({ severity: 'success', summary: '', detail: t('notification.add_successfully'), life: 3000 });
    inputType.value = '';
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  }
};
</script>

<template>
  <Toast position="top-left" group="tl" />
  <Splitter v-if="payload">
    <SplitterPanel>
      <!-- Deposit due -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.depositedueEn')"
        :titleUrlFr="$t('promos.email.depositedueFr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.DEPOSIT_DUE_EN"
        v-model:linkFr="link.DEPOSIT_DUE_FR"
        :typeLink="DEPOSIT_DUE_EN"
        :typeLinkFr="DEPOSIT_DUE_FR"
        :itemUrl="itemUrl?.depositDueEn"
        :itemUrlFr="itemUrl?.depositDueFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- CHANGE DEPARTMENT -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.process_en')"
        :titleUrlFr="$t('promos.email.process_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.DEPT_TO_DEPT_EN"
        v-model:linkFr="link.DEPT_TO_DEPT_FR"
        :typeLink="DEPT_TO_DEPT_EN"
        :typeLinkFr="DEPT_TO_DEPT_FR"
        :itemUrl="itemUrl?.deptToDeptEn"
        :itemUrlFr="itemUrl?.deptToDeptFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- QUOTE EXPIRING -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.expires_en')"
        :titleUrlFr="$t('promos.email.expires_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.EXPIRES_EN"
        v-model:linkFr="link.EXPIRES_FR"
        :typeLink="EXPIRES_EN"
        :typeLinkFr="EXPIRES_FR"
        :itemUrl="itemUrl?.expiresEn"
        :itemUrlFr="itemUrl?.expiresFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- QUOTE EXPIRED -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.expired_en')"
        :titleUrlFr="$t('promos.email.expired_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.EXPIRED_EN"
        v-model:linkFr="link.EXPIRED_FR"
        :typeLink="EXPIRED_EN"
        :typeLinkFr="EXPIRED_FR"
        :itemUrl="itemUrl?.expiredEn"
        :itemUrlFr="itemUrl?.expiredFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- pdf sent -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.pdf_sent_en')"
        :titleUrlFr="$t('promos.email.pdf_sent_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.PDF_SENT_EN"
        v-model:linkFr="link.PDF_SENT_FR"
        :typeLink="PDF_SENT_EN"
        :typeLinkFr="PDF_SENT_FR"
        :itemUrl="itemUrl?.pdfSentEn"
        :itemUrlFr="itemUrl?.pdfSentFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- payment is due -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.payment_is_due_en')"
        :titleUrlFr="$t('promos.email.payment_is_due_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.PAYMENT_IS_DUE_EN"
        v-model:linkFr="link.PAYMENT_IS_DUE_FR"
        :typeLink="PAYMENT_IS_DUE_EN"
        :typeLinkFr="PAYMENT_IS_DUE_FR"
        :itemUrl="itemUrl?.paymentIsDueEn"
        :itemUrlFr="itemUrl?.paymentIsDueFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- names required -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.name_required_en')"
        :titleUrlFr="$t('promos.email.name_required_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.NAME_REQUIRED_EN"
        v-model:linkFr="link.NAME_REQUIRED_FR"
        :typeLink="NAME_REQUIRED_EN"
        :typeLinkFr="NAME_REQUIRED_FR"
        :itemUrl="itemUrl?.nameRequiredEn"
        :itemUrlFr="itemUrl?.nameRequiredFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- payment success -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.payment_success_en')"
        :titleUrlFr="$t('promos.email.payment_success_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.PAYMENT_SUCCESS_EN"
        v-model:linkFr="link.PAYMENT_SUCCESS_FR"
        :typeLink="PAYMENT_SUCCESS_EN"
        :typeLinkFr="PAYMENT_SUCCESS_FR"
        :itemUrl="itemUrl?.paymentSuccessEn"
        :itemUrlFr="itemUrl?.paymentSuccessFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- Selling Price Unavailable -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.selling_price_unavailable_en')"
        :titleUrlFr="$t('promos.email.selling_price_unavailable_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.MISSING_MARGIN_EN"
        v-model:linkFr="link.MISSING_MARGIN_FR"
        :typeLink="MISSING_MARGIN_EN"
        :typeLinkFr="MISSING_MARGIN_FR"
        :itemUrl="itemUrl?.missingMarginEn"
        :itemUrlFr="itemUrl?.missingMarginFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- Brochure Error -->
      <AdminSetupUrl
        :titleUrl="$t('promos.email.brochure_error_en')"
        :titleUrlFr="$t('promos.email.brochure_error_fr')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.BROCHURE_URL_EN"
        v-model:linkFr="link.BROCHURE_URL_FR"
        :typeLink="BROCHURE_URL_EN"
        :typeLinkFr="BROCHURE_URL_FR"
        :itemUrl="itemUrl?.brochureUrlEn"
        :itemUrlFr="itemUrl?.brochureUrlFr"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- RQ to book -->
      <AdminSetupUrl
        :titleUrl="$t('quote_status.rq_to_book')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.DEPT_TO_DEPT_INCENTIVE"
        :typeLink="DEPT_TO_DEPT_INCENTIVE"
        :itemUrl="itemUrl?.deptToDeptNotIncentiveEn"
        @handleSubmit="handleSubmit($event)"
      />

      <!-- New hotel alert -->
      <AdminSetupUrl
        :titleUrl="$t('gqt_admin_setup.email_url_configuration.new_hotel_alert')"
        :canEdit="canEditAdminSetup"
        v-model:link="link.ALERT_SYNC_HOTEL"
        :typeLink="ALERT_SYNC_HOTEL"
        :itemUrl="itemUrl?.alertSyncHotel"
        @handleSubmit="handleSubmit($event)"
      />
    </SplitterPanel>
  </Splitter>
</template>
